import { useEffect, useRef, useState } from "react";

/**
 * Animates the first integer found in a string from 0 to its target on mount
 * (and whenever `value` changes). Preserves surrounding characters, so it
 * works for values like "49", "$0", "1,234", "3/5", "0/1".
 *
 * If no numeric part is found, renders the value as-is.
 */
export default function AnimatedValue({ value, duration = 900 }) {
  const str = value === null || value === undefined ? "" : String(value);
  const match = str.match(/-?\d[\d,]*/);
  const target = match ? parseInt(match[0].replace(/,/g, ""), 10) : null;
  const hasTarget = target !== null && !Number.isNaN(target);
  const [current, setCurrent] = useState(hasTarget ? 0 : null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!hasTarget) return;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setCurrent(Math.round(eased * target));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, hasTarget, duration]);

  if (!hasTarget) return <>{str}</>;
  const formatted = (current ?? 0).toLocaleString();
  return <>{str.replace(match[0], formatted)}</>;
}
